import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Info } from "lucide-react";
import { MEDICAL_QUESTIONS, COSMETIC_INGREDIENTS } from "@/lib/medicalQuestionnaire";

// Guest-facing Pre-Treatment Medical Questionnaire.
//
// Same schema as the staff Profile tab so both sides land on the same
// customer doc (`medication_allergies`, `cosmetic_allergies`,
// `current_medications`, `medical_questionnaire`). Saving fires a staff
// notification email (see backend `_notify_medical_update`).
//
// The customer can come back and edit any field at any time — every save
// stamps `medical_updated_at` and re-notifies the staff, which is by design
// (spec: "make this form can be revised or edited by customer anytime").
export default function CustomerMedical() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    medication_allergies: "",
    cosmetic_allergies: [],
    current_medications: "",
    medical_questionnaire: {},
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastUpdateBy, setLastUpdateBy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/customers/me/medical");
      const d = r.data || {};
      setForm({
        medication_allergies: d.medication_allergies || "",
        cosmetic_allergies: Array.isArray(d.cosmetic_allergies) ? d.cosmetic_allergies : [],
        current_medications: d.current_medications || "",
        medical_questionnaire: (d.medical_questionnaire && typeof d.medical_questionnaire === "object") ? d.medical_questionnaire : {},
      });
      setLastUpdate(d.medical_updated_at || null);
      setLastUpdateBy(d.medical_updated_by || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await customerApi.put("/customers/me/medical", form);
      const d = r.data || {};
      setLastUpdate(d.medical_updated_at || null);
      setLastUpdateBy(d.medical_updated_by || null);
      toast.success("Medical form saved · your therapist has been notified");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setAnswer = (key, ans) => {
    const next = { ...(form.medical_questionnaire || {}) };
    next[key] = { ...(next[key] || {}), answer: ans };
    setForm({ ...form, medical_questionnaire: next });
  };
  const setNote = (key, note) => {
    const next = { ...(form.medical_questionnaire || {}) };
    next[key] = { ...(next[key] || {}), note };
    setForm({ ...form, medical_questionnaire: next });
  };

  const formatBali = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Makassar", dateStyle: "medium", timeStyle: "short" });
    } catch { return null; }
  };

  return (
    <div>
      <CustomerNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => nav("/portal/profile")}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          data-testid="medical-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to profile
        </button>
        <p className="tracking-overline text-[11px] text-muted-foreground">HEALTH</p>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-2 sm:mt-3 mb-2">Medical questionnaire</h1>
        <p className="text-sm text-muted-foreground mb-2 max-w-xl">
          Share your medical history so our therapists can plan a safe treatment. You can update this any time — every change is sent to your therapist.
        </p>
        {lastUpdate && (
          <p className="text-[11px] text-muted-foreground mb-6" data-testid="medical-last-update">
            Last updated {formatBali(lastUpdate)}{lastUpdateBy ? ` by ${lastUpdateBy}` : ""}
          </p>
        )}

        <Card className="rounded-2xl">
          <CardContent className="p-5 sm:p-8 space-y-6">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div>
                  <Label htmlFor="med-allergies">Medication Allergies</Label>
                  <Textarea
                    id="med-allergies"
                    value={form.medication_allergies}
                    onChange={(e) => setForm({ ...form, medication_allergies: e.target.value })}
                    rows={2}
                    placeholder="e.g. Penicillin, Sulfa drugs, Latex…"
                    className="mt-1"
                    data-testid="med-allergies-input"
                  />
                </div>

                <div>
                  <Label>Cosmetic Allergy Ingredients</Label>
                  <p className="text-[11px] text-muted-foreground mb-2">Tick every ingredient you react to.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2" data-testid="cosmetic-allergies-grid">
                    {COSMETIC_INGREDIENTS.map((ing) => {
                      const checked = (form.cosmetic_allergies || []).includes(ing);
                      return (
                        <label
                          key={ing}
                          className="flex items-center gap-2 text-sm cursor-pointer select-none"
                          data-testid={`cosmetic-${ing.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const cur = new Set(form.cosmetic_allergies || []);
                              if (v) cur.add(ing); else cur.delete(ing);
                              setForm({ ...form, cosmetic_allergies: Array.from(cur) });
                            }}
                          />
                          <span>{ing}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="cur-meds">Current Prescription &amp; Over-the-Counter Medications</Label>
                  <p className="text-[11px] text-muted-foreground mb-1">Include Aspirin, Ibuprofen, herbs, vitamins.</p>
                  <Textarea
                    id="cur-meds"
                    value={form.current_medications}
                    onChange={(e) => setForm({ ...form, current_medications: e.target.value })}
                    rows={2}
                    placeholder="e.g. Aspirin 100mg daily, multivitamin, fish oil…"
                    className="mt-1"
                    data-testid="cur-meds-input"
                  />
                </div>

                <div className="pt-2">
                  <Label>Pre-treatment questionnaire</Label>
                  <p className="text-[11px] text-muted-foreground mb-2">Tick Yes or No for each. Add a comment if there&apos;s more to know.</p>
                  <div className="rounded-md border border-border overflow-hidden">
                    <div className="grid grid-cols-[1fr_50px_50px_1.2fr] gap-2 px-3 py-2 bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <span>Do you have</span><span className="text-center">Yes</span><span className="text-center">No</span><span>Comments</span>
                    </div>
                    {MEDICAL_QUESTIONS.map((q, idx) => {
                      const val = form.medical_questionnaire?.[q.key] || {};
                      return (
                        <div
                          key={q.key}
                          className={
                            "grid grid-cols-[1fr_50px_50px_1.2fr] gap-2 px-3 py-2 text-sm items-center " +
                            (idx > 0 ? "border-t border-border/50 " : "")
                          }
                          data-testid={`mq-row-${q.key}`}
                        >
                          <span className="pr-2 text-xs sm:text-sm leading-snug">{q.label}</span>
                          <RadioGroup
                            value={val.answer || ""}
                            onValueChange={(v) => setAnswer(q.key, v)}
                            className="contents"
                          >
                            <div className="flex justify-center">
                              <RadioGroupItem value="yes" id={`mq-${q.key}-yes`} data-testid={`mq-${q.key}-yes`} />
                            </div>
                            <div className="flex justify-center">
                              <RadioGroupItem value="no" id={`mq-${q.key}-no`} data-testid={`mq-${q.key}-no`} />
                            </div>
                          </RadioGroup>
                          <Input
                            value={val.note || ""}
                            onChange={(e) => setNote(q.key, e.target.value)}
                            placeholder="Comments…"
                            className="h-8"
                            data-testid={`mq-note-${q.key}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>Feel free to come back and update any answer whenever your health changes. Your therapist is notified each time.</span>
                </div>

                <div className="flex justify-end">
                  <Button
                    className="rounded-full"
                    onClick={save}
                    disabled={busy}
                    data-testid="medical-save-btn"
                  >
                    {busy ? "Saving…" : "Save & notify therapist"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
